import { inject, untracked } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { Subscription } from 'rxjs';

import { AssetType } from '@core/api/dashboard.enums';
import { Asset, SiteStructureResponse } from '@core/api/dashboard.models';
import { DashboardService } from '@core/api/dashboard.service';

interface SiteStructureCacheEntry {
  structure?: SiteStructureResponse;
  isLoading: boolean;
  error?: string;
}

interface SiteStructureState {
  entriesBySiteId: Record<string, SiteStructureCacheEntry | undefined>;
  cachedSiteIds: string[];
}

const initialState: SiteStructureState = {
  entriesBySiteId: {},
  cachedSiteIds: [],
};

export const SiteStructureStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store) => {
    const dashboardService = inject(DashboardService);
    const requests = new Set<Subscription>();
    const requestTokens = new Map<string, symbol>();

    const setEntry = (siteId: string, entry: SiteStructureCacheEntry | undefined): void => {
      patchState(store, {
        entriesBySiteId: {
          ...store.entriesBySiteId(),
          [siteId]: entry,
        },
      });
    };

    const load = (siteIds: string[], force: boolean): void => {
      const siteIdsToLoad = [...new Set(siteIds)].filter((siteId) => {
        const currentEntry = untracked(() => store.entriesBySiteId()[siteId]);
        return force || (!currentEntry?.structure && !currentEntry?.isLoading);
      });
      if (siteIdsToLoad.length === 0) {
        return;
      }

      const tokensBySiteId = new Map<string, symbol>();
      siteIdsToLoad.forEach((siteId) => {
        const requestToken = Symbol(siteId);
        requestTokens.set(siteId, requestToken);
        tokensBySiteId.set(siteId, requestToken);
        const currentEntry = untracked(() => store.entriesBySiteId()[siteId]);
        setEntry(siteId, { structure: currentEntry?.structure, isLoading: true });
      });

      const request = dashboardService.getSiteStructures$(siteIdsToLoad).subscribe({
        next: (structures) => {
          const entriesBySiteId = { ...store.entriesBySiteId() };
          const cachedSiteIds = new Set(store.cachedSiteIds());
          structures.forEach((structure) => {
            if (requestTokens.get(structure.siteId) !== tokensBySiteId.get(structure.siteId)) {
              return;
            }
            entriesBySiteId[structure.siteId] = { structure, isLoading: false };
            cachedSiteIds.add(structure.siteId);
          });
          patchState(store, { entriesBySiteId, cachedSiteIds: [...cachedSiteIds] });
        },
        error: () => {
          const entriesBySiteId = { ...store.entriesBySiteId() };
          siteIdsToLoad.forEach((siteId) => {
            if (requestTokens.get(siteId) !== tokensBySiteId.get(siteId)) {
              return;
            }
            entriesBySiteId[siteId] = {
              structure: entriesBySiteId[siteId]?.structure,
              isLoading: false,
              error: 'Failed to load site structure',
            };
          });
          patchState(store, { entriesBySiteId });
        },
        complete: () => {
          siteIdsToLoad.forEach((siteId) => {
            if (requestTokens.get(siteId) === tokensBySiteId.get(siteId)) {
              requestTokens.delete(siteId);
            }
          });
          queueMicrotask(() => requests.delete(request));
        },
      });
      if (siteIdsToLoad.some((siteId) => requestTokens.get(siteId) === tokensBySiteId.get(siteId))) {
        requests.add(request);
      }
    };

    return {
      ensureSiteStructureLoaded(siteIds: string[]): void {
        load(siteIds, false);
      },
      reloadSiteStructure(siteId: string): void {
        load([siteId], true);
      },
      clearSiteStructure(siteId: string): void {
        requestTokens.delete(siteId);
        const { [siteId]: _, ...entriesBySiteId } = store.entriesBySiteId();
        patchState(store, { entriesBySiteId, cachedSiteIds: store.cachedSiteIds().filter((cachedSiteId) => cachedSiteId !== siteId) });
      },
      clear(): void {
        requests.forEach((request) => request.unsubscribe());
        requests.clear();
        requestTokens.clear();
        patchState(store, initialState);
      },
      siteStructureForSite(siteId: string): SiteStructureResponse | undefined {
        return store.entriesBySiteId()[siteId]?.structure;
      },
      siteStructureHasLoadedForSite(siteId: string): boolean {
        return store.cachedSiteIds().includes(siteId);
      },
      siteStructureIsLoadingForSite(siteId: string): boolean {
        return store.entriesBySiteId()[siteId]?.isLoading ?? false;
      },
      siteStructureErrorForSite(siteId: string): string | undefined {
        return store.entriesBySiteId()[siteId]?.error;
      },
      assetsForSiteByType(siteId: string, assetType: AssetType): Asset[] | undefined {
        return store.entriesBySiteId()[siteId]?.structure?.assets.filter((asset) => asset.assetType === assetType);
      },
      assetForSite(siteId: string, assetId: string): Asset | undefined {
        return store.entriesBySiteId()[siteId]?.structure?.assets.find((asset) => asset.id === assetId);
      },
      assetNameForSite(siteId: string, assetId: string): string | undefined {
        return store.entriesBySiteId()[siteId]?.structure?.assets.find((asset) => asset.id === assetId)?.name;
      },
    };
  })
);

export type SiteStructureStoreInstance = InstanceType<typeof SiteStructureStore>;
